import React from 'react';
import { Dropdown, Button, Tooltip, theme } from 'antd';
import { IconLayoutGrid } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { useServiceProviderProfile } from '../../contextProviders/ServiceProviderProfileProvider';
import { visibleHostTools, hostToolPath, isHostToolActive } from '../../constants/hostToolsNav';
import { useIncomingBookingsBadge } from '../../contextProviders/IncomingBookingsProvider';
import { useIncomingServiceRequestsBadge } from '../../contextProviders/IncomingServiceRequestsProvider';
import NavBadge from '../NavBadge';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

/**
 * The host's own screens, behind one icon in the desktop header.
 *
 * They used to sit in the header's menu bar, all of them, next to Explorar
 * and Reservas: nine top-level links, of which most were catalogue screens a
 * host opens now and then. The phone had solved this long ago -- its bottom
 * bar keeps a few tabs and puts the rest behind one button (see
 * MobileBottomNav's own sheet) -- and this is the same shape, from the same
 * list.
 *
 * Reservas is one of these rows now, which is why the unseen-incoming badge
 * rides on the trigger icon as well: with the label hidden behind a click, a
 * host would otherwise have no sign from the header that anything arrived.
 * Presupuestos is the same story for the other half of the platform, and the
 * icon carries the sum of the two.
 */
const HostToolsMenu: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasHostProfile } = useHostProfile();
  const { hasServiceProviderProfile } = useServiceProviderProfile();
  const { unseenCount } = useIncomingBookingsBadge();
  const { unseenCount: unseenRequests } = useIncomingServiceRequestsBadge();

  const tools = visibleHostTools({ hasHostProfile, hasServiceProviderProfile });
  const activeKey = tools.find((tool) => isHostToolActive(tool, location.pathname))?.key;

  // What each row is worth counting, so the rendering below can stay one
  // expression instead of a special case per row.
  const rowBadge = (key: string): number => {
    if (key === 'bookings') return hasHostProfile ? unseenCount : 0;
    if (key === 'quotes') return hasServiceProviderProfile ? unseenRequests : 0;
    return 0;
  };

  // The trigger adds them up: with every label behind a click, one number on
  // the icon is the only sign the header gives that anything arrived at all,
  // and which of the two it was is a row away.
  const triggerCount = rowBadge('bookings') + rowBadge('quotes');

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        selectedKeys: activeKey ? [activeKey] : [],
        items: tools.map(({ key, Icon, labelKey }) => ({
          key,
          icon: <Icon size={17} />,
          label: rowBadge(key) > 0 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {translate(t, labelKey)}
              <NavBadge count={rowBadge(key)} />
            </span>
          ) : (
            translate(t, labelKey)
          ),
        })),
        onClick: ({ key }) => {
          const tool = tools.find((item) => item.key === key);
          if (tool) navigate(hostToolPath(tool, hasHostProfile));
        },
      }}
    >
      <Tooltip title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_HOST_TOOLS)}>
        <span style={{ display: 'inline-flex', position: 'relative' }}>
          <Button
            type="text"
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_HOST_TOOLS)}
            icon={<IconLayoutGrid size={18} />}
            // Tinted while one of these screens is the one on show, so the
            // icon still says where you are now that the labels are hidden.
            style={activeKey ? { background: token.colorPrimaryBg, color: token.colorPrimary } : undefined}
          />
          {triggerCount > 0 && (
            <span style={{ position: 'absolute', top: -2, right: -4, pointerEvents: 'none' }}>
              <NavBadge count={triggerCount} />
            </span>
          )}
        </span>
      </Tooltip>
    </Dropdown>
  );
};

export default HostToolsMenu;
